import { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CalcSteps } from "@/components/calc/CalcSteps";
import { LENGTH_BUCKETS, goalProgress, limitStatuses, readingEase, sentenceLengthView } from "./wordCounterExtras";

// Sentence length is ordered data, so it is shown as one theme-teal ramp (short = faint, long = strong) instead of six hues.
// The legend counts and each sentence's tooltip also say the group in words, so colour is never the only signal.
const BUCKET_CLASS = [
  "bg-primary/5",
  "bg-primary/10",
  "bg-primary/15",
  "bg-primary/25",
  "bg-primary/35",
  "bg-primary/45",
];

interface Props {
  text: string;
  words: number;
  chars: number;
  sentences: number;
  counting: boolean;
  /** The text is too long for the sentence view and reading score (they are skipped, not slow). */
  tooBig: boolean;
  goal: string;
  onGoalChange: (v: string) => void;
}

export default function WordCounterExtras({ text, words, chars, sentences, counting, tooBig, goal, onGoalChange }: Props) {
  const [showHighlight, setShowHighlight] = useState(false);
  const limits = useMemo(() => limitStatuses(chars), [chars]);
  const progress = goalProgress(words, Number(goal));
  const view = useMemo(() => (tooBig || !text.trim() ? null : sentenceLengthView(text)), [text, tooBig]);
  const ease = useMemo(() => (tooBig || !text.trim() ? null : readingEase(text, sentences)), [text, sentences, tooBig]);
  const hasText = text.trim().length > 0;

  return (
    <div className="mt-8 space-y-8" data-testid="wc-extras">
      <section aria-labelledby="wc-goal-h">
        <h2 id="wc-goal-h" className="text-sm font-semibold">Word goal</h2>
        <div className="mt-2 flex items-center gap-3">
          <Label htmlFor="wc-goal" className="text-sm">Target words</Label>
          <Input id="wc-goal" type="number" inputMode="numeric" min={1} max={10000000} className="w-32" value={goal} onChange={(e) => onGoalChange(e.target.value)} placeholder="e.g. 500" />
        </div>
        {progress && !counting && (
          <div className="mt-3" data-testid="wc-goal-progress">
            <div role="progressbar" aria-label="Progress to word goal" aria-valuemin={0} aria-valuemax={100} aria-valuenow={progress.percent} className="h-2 w-full rounded bg-muted overflow-hidden">
              <div className={`h-full ${progress.reached ? "bg-green-600" : "bg-primary"}`} style={{ width: `${progress.percent}%` }} />
            </div>
            <p className="mt-1 text-sm">{progress.words} of {progress.target} words ({progress.percent}%). <strong>{progress.text}</strong></p>
          </div>
        )}
        <p className="mt-1 text-xs text-muted-foreground">Your goal lives only on this page and is forgotten when you close or reload it.</p>
      </section>

      <section aria-labelledby="wc-limits-h">
        <h2 id="wc-limits-h" className="text-sm font-semibold">Platform limits</h2>
        <p className="text-xs text-muted-foreground mt-1">Characters used: {counting ? "…" : chars}. Platforms change their limits, so confirm on the platform. X counts every link as 23 characters; this counts plain characters.</p>
        <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm" data-testid="wc-limits">
          {limits.map((l) => (
            <li key={l.id} className="flex justify-between gap-3 border-b py-1" data-over={l.over ? "true" : "false"}>
              <span>{l.label} <span className="text-muted-foreground">({l.limit}{l.soft ? ", guide" : ""})</span></span>
              <span className={counting || !hasText ? "text-muted-foreground" : l.over ? "font-semibold text-red-700 dark:text-red-400" : "text-green-700 dark:text-green-400"}>
                {counting || !hasText ? "-" : l.over ? `Over: ${l.text}` : `OK: ${l.text}`}
              </span>
            </li>
          ))}
        </ul>
      </section>

      {tooBig && hasText && <p className="text-xs text-muted-foreground" role="status">The sentence-length view and reading score are skipped for very long text. Counts, goal and limits above still work.</p>}

      {ease && (
        <section aria-labelledby="wc-ease-h" data-testid="wc-ease">
          <h2 id="wc-ease-h" className="text-sm font-semibold">Reading ease</h2>
          {ease.applicable ? (
            <>
              <p className="mt-2 text-sm"><strong data-testid="wc-flesch">{ease.flesch}</strong> Flesch Reading Ease ({ease.label}) &middot; Flesch-Kincaid {ease.grade} ({ease.gradeLabel})</p>
              <div className="mt-2"><CalcSteps steps={ease.steps} /></div>
            </>
          ) : (
            <p className="mt-2 text-xs text-muted-foreground">{ease.reason}</p>
          )}
        </section>
      )}

      {view && view.sentences.length > 0 && (
        <section aria-labelledby="wc-len-h" data-testid="wc-sentence-lengths">
          <h2 id="wc-len-h" className="text-sm font-semibold">Sentence lengths</h2>
          <p className="text-xs text-muted-foreground mt-1">Average {view.average} words, longest {view.longest}. Mixing short and long sentences reads best. The shading gets stronger as sentences get longer.</p>
          <ul className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
            {LENGTH_BUCKETS.map((b, i) => (
              <li key={b.id} className={`rounded border px-2 py-1 ${BUCKET_CLASS[i]}`}><span className="font-medium">{view.counts[i]}</span> <span className="text-muted-foreground">sentences of {b.label}</span></li>
            ))}
          </ul>
          <label className="mt-3 flex items-center gap-2 text-sm">
            <input type="checkbox" checked={showHighlight} onChange={(e) => setShowHighlight(e.target.checked)} /> Highlight sentences by length in my text
          </label>
          {showHighlight && (
            <p className="mt-2 rounded border p-3 text-sm leading-relaxed whitespace-pre-wrap" data-testid="wc-highlight">
              {view.sentences.map((s, i) => (
                <span key={i} title={`${s.words} words (${LENGTH_BUCKETS[s.bucket].label})`} className={`${BUCKET_CLASS[s.bucket]} rounded px-0.5`}>{s.text}{" "}</span>
              ))}
            </p>
          )}
        </section>
      )}
    </div>
  );
}
